import React from "react";

// Premium card: theme-aware via CSS vars. Use everywhere instead of bg-white + border + rounded ad-hoc.
//
// Variants:
//   default  — standard surface card
//   elevated — for floating dashboard cards (more shadow)
//   subtle   — no border, just bg.subtle (for nested groupings)
//   ghost    — transparent (for containers)
//
// Padding tokens: "none" | "sm" | "md" | "lg"

const PAD = { none: "0", sm: "12px", md: "16px", lg: "24px" };

export default function Card({
  variant = "default",
  padding = "md",
  interactive = false,
  className = "",
  style = {},
  children,
  onClick,
  ...rest
}) {
  const base = {
    borderRadius: "14px",
    padding: PAD[padding] ?? PAD.md,
    transition: "box-shadow 200ms cubic-bezier(0.4,0,0.2,1), transform 200ms cubic-bezier(0.4,0,0.2,1), border-color 200ms",
    cursor: onClick || interactive ? "pointer" : undefined,
  };

  const variants = {
    default: {
      background: "var(--bg-surface)",
      border: "1px solid var(--border-subtle)",
      boxShadow: "var(--shadow-sm)",
    },
    elevated: {
      background: "var(--bg-elevated)",
      border: "1px solid var(--border-subtle)",
      boxShadow: "var(--shadow-md)",
    },
    subtle: {
      background: "var(--bg-subtle)",
      border: "1px solid transparent",
    },
    ghost: {
      background: "transparent",
    },
  };

  const hover = (onClick || interactive) ? {
    onMouseEnter: (e) => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.transform = "translateY(-1px)"; },
    onMouseLeave: (e) => { e.currentTarget.style.boxShadow = variants[variant].boxShadow || "none"; e.currentTarget.style.transform = "translateY(0)"; },
  } : {};

  return (
    <div
      className={className}
      style={{ ...base, ...variants[variant], ...style }}
      onClick={onClick}
      {...hover}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, action, children, style = {} }) {
  if (children) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, ...style }}>{children}</div>
  );
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, ...style }}>
      <div>
        {title && <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>{title}</div>}
        {subtitle && <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 2 }}>{subtitle}</div>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}
